import { useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Save, Pill } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = [['contrast', 'Contrast'], ['local_anaesthetic', 'Local anaesthetic'], ['steroid', 'Steroid'], ['other', 'Other']];
const EMPTY = { category: 'contrast', product: '', batch_number: '', expiry_date: '', single_dose: true };
const cls = 'w-full text-xs bg-background border border-border rounded-md px-2 py-1.5';

// Per-procedure traceability — batch / lot numbers of everything injected (NSW Health HA26-4074).
export function MedicationsUsedPanel({ submission, color, onSaved }) {
  const [items, setItems] = useState(submission.medications_used?.length ? submission.medications_used : [{ ...EMPTY }]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const set = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      await axios.put(`${API}/consents/${submission.submission_id}/medications`,
        { items: items.filter(i => i.product.trim() || i.batch_number.trim()) }, { withCredentials: true });
      setMsg('Saved'); onSaved?.();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  return (
    <div className="mt-6 rounded-2xl border bg-card p-4 print-hidden" data-testid="meds-used-panel">
      <p className="text-[10px] font-bold uppercase tracking-[0.16em] inline-flex items-center gap-1.5" style={{ color }}>
        <Pill className="w-3.5 h-3.5" /> Medications &amp; contrast used — batch numbers
      </p>
      <p className="text-[11px] text-muted-foreground mt-1">One vial, one patient. Record every contrast, local anaesthetic and steroid injected.</p>
      <div className="space-y-2 mt-3">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-2 md:grid-cols-[130px_1fr_140px_130px_110px_32px] gap-2 items-center" data-testid={`meds-row-${i}`}>
            <select value={it.category} onChange={e => set(i, 'category', e.target.value)} className={cls} data-testid={`meds-cat-${i}`}>
              {CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input value={it.product} onChange={e => set(i, 'product', e.target.value)} placeholder="Product (e.g. Omnipaque 300)" className={cls} data-testid={`meds-product-${i}`} />
            <input value={it.batch_number} onChange={e => set(i, 'batch_number', e.target.value)} placeholder="Batch / lot no." className={cls} data-testid={`meds-batch-${i}`} />
            <input type="date" value={it.expiry_date} onChange={e => set(i, 'expiry_date', e.target.value)} className={cls} data-testid={`meds-expiry-${i}`} />
            <label className="text-[11px] inline-flex items-center gap-1">
              <input type="checkbox" checked={it.single_dose} onChange={e => set(i, 'single_dose', e.target.checked)} data-testid={`meds-single-${i}`} /> Single-dose
            </label>
            <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="p-1 rounded hover:bg-accent" data-testid={`meds-remove-${i}`}>
              <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button type="button" onClick={() => setItems([...items, { ...EMPTY }])} data-testid="meds-add"
          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add item</button>
        <button type="button" onClick={save} disabled={busy} data-testid="meds-save"
          className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1 disabled:opacity-50" style={{ backgroundColor: color }}>
          <Save className="w-3.5 h-3.5" /> {busy ? 'Saving…' : 'Save'}
        </button>
        {msg && <span className="text-xs text-muted-foreground" data-testid="meds-msg">{msg}</span>}
      </div>
    </div>
  );
}

// Printed under the consent so the RIS copy carries the batch numbers.
export function MedicationsUsedPrint({ submission }) {
  const items = submission.medications_used || [];
  if (!items.length) return null;
  const label = Object.fromEntries(CATS);
  return (
    <div className="consent-doc" style={{ marginTop: 12 }} data-testid="meds-used-print">
      <h2>Procedure record — medications &amp; contrast used</h2>
      <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
        <thead><tr style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1' }}>
          <th>Type</th><th>Product</th><th>Batch / lot</th><th>Expiry</th><th>Single-dose</th></tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td>{label[it.category] || it.category}</td><td>{it.product}</td><td>{it.batch_number}</td>
              <td>{it.expiry_date ? it.expiry_date.split('-').reverse().join('/') : ''}</td><td>{it.single_dose ? 'Yes' : 'No — see vial register'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="small" style={{ marginTop: 4 }}>
        Recorded by {submission.medications_recorded_by?.name || '—'}
        {submission.medications_recorded_at ? ` · ${new Date(submission.medications_recorded_at).toLocaleString('en-AU')}` : ''}
      </p>
    </div>
  );
}
